import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { getPathValue } from '@osos/shared/legacy-core';
import * as isEqual from 'fast-deep-equal';
import uniqBy from 'lodash-es/uniqBy';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import {
  FormFieldControl,
  injectNgControlRef,
  NgControlRef,
} from '../forms-utils';
import { SELECT_FOOTER_CONTENT } from './select-footer-content.directive';
import { SELECT_GROUP_CONTENT } from './select-group-content.directive';
import { SELECT_ITEM_CONTENT } from './select-item-content.directive';
import { SELECTED_ITEM_CONTENT } from './selected-item-content.directive';
import { OverlayOptions } from 'primeng/api';
import { Z_INDEX_POPOVER } from '@osos/shared/types';

let nextUniqueId = 0;

// TODO: Refactor this component to replace the functions calls from template.

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  hostDirectives: [NgControlRef],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
    {
      provide: FormFieldControl,
      useExisting: SelectComponent,
    },
  ],
  standalone: false,
})
export class SelectComponent
  implements OnChanges, ControlValueAccessor, FormFieldControl
{
  /**
   * @property{any} holds the whole object of a selected value in single mode
   */
  selectedResult: any;
  /**
   * @property{any[]} holds the array of objects of selected values in multiple mode
   */
  selectedResults: any[] = [];
  /**
   * @property{string} holds only the selected property value in single mode @see optionValue @see isMultipleEnabled
   */
  selectedOptionValue: any;
  /**
   * @property{any[]} holds only the selected property values in multiple mode @see optionValue @see isMultipleEnabled
   */
  selectedOptionValues: any[] = [];
  /**
   * @property{any[]} holds only the filtered options in the frontend filtering @see isCustomFilter
   */
  filteredOptions: any[] = [];

  /** private configuration and coercion**/
  private _isPInputOutlinedEnabled = false;
  private _isGroupEnabled = false;
  private _isFilterEnabled = true;
  private _isShowClearEnabled = false;
  private _isMultipleEnabled = false;
  private _isCustomFilter = false;
  private _autofocus = false;
  private _searchTerm = '';
  /** Configuration part **/

  @Input() autoDisplayFirst: boolean;
  @Input() bindValue: string | ((arg: any) => any);
  @Input() bindKey: string;
  @Input() bindLabel: string | ((arg: any) => any);
  @Input() bindGroupLabel: string | ((arg: any) => any);
  @Input() bindAvatar: string | ((arg: any) => any);
  @Input() bindCaption: string | ((arg: any) => any);
  @Input() bindIcon: string | ((arg: any) => any);
  @Input() appendTo = 'body';
  @Input() placeholder: string;
  @Input() filterPlaceholder: string;
  @Input() styleClass: string;
  @Input() panelStyleClass: string;
  @Input() emptyFilterMessage: string;
  @Input() emptyMessage: string;
  @Input() noSelectedMessage: string;
  @Input() selectedMessage: string;
  @Input() filterBy = '';
  @Input() inputId = `select-input-${nextUniqueId++}`;
  @Input() optionDisabled: string;
  @Input() selected: any | any[];
  @Input() itemStyleClass: string;
  @Input() loading = false;

  @Output() onSelectionChanged: EventEmitter<any | any[]> = new EventEmitter<
    any | any[]
  >();
  @Output() onFilterChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() onBlur: EventEmitter<any> = new EventEmitter<any>();
  @Output() onShow: EventEmitter<any> = new EventEmitter<any>();
  /**
   * @property {string} model property name which is used to filter/search
   */
  @Input() searchProperty: string;
  /** Configuration getters/setters **/
  /**
   *  @property {any[]} local side filtering option, this will only work when there is no customFilter attribute
   *
   */
  @Input() options: any[];
  /**
   * @property {boolean} used to enable search/filtering on the select input
   */
  @Input('filter')
  set isFilterEnabled(value: BooleanInput) {
    this._isFilterEnabled = coerceBooleanProperty(value);
  }
  get isFilterEnabled(): boolean {
    return this._isFilterEnabled;
  }

  /**
   * @property {boolean} shows cleat button to clear the results
   */
  @Input('showClear')
  set isShowClearEnabled(value: BooleanInput) {
    this._isShowClearEnabled = coerceBooleanProperty(value);
  }
  get isShowClearEnabled(): boolean {
    return this._isShowClearEnabled;
  }

  @Input('pInputOutlined')
  get isPInputOutlinedEnabled() {
    return this._isPInputOutlinedEnabled;
  }
  set isPInputOutlinedEnabled(value: BooleanInput) {
    this._isPInputOutlinedEnabled = coerceBooleanProperty(value);
  }

  @Input('group')
  get isGroupEnabled(): boolean {
    return this._isGroupEnabled;
  }
  set isGroupEnabled(value: BooleanInput) {
    this._isGroupEnabled = coerceBooleanProperty(value);
  }

  /**
   * @property {boolean} used to enable multi select option
   */
  @Input('multiple')
  get isMultipleEnabled() {
    return this._isMultipleEnabled;
  }
  set isMultipleEnabled(value: BooleanInput) {
    this._isMultipleEnabled = coerceBooleanProperty(value);
  }

  /**
   * @property {boolean} used to enable the server side filtering
   */
  @Input('customFilter')
  get isCustomFilter(): boolean {
    return this._isCustomFilter;
  }
  set isCustomFilter(value: BooleanInput) {
    this._isCustomFilter = coerceBooleanProperty(value);
  }
  @Input()
  get autofocus(): boolean {
    return this._autofocus;
  }
  set autofocus(value: BooleanInput) {
    this._autofocus = coerceBooleanProperty(value);
  }

  @Input() disabled = false;

  /** Template parts **/
  @ContentChild(SELECT_ITEM_CONTENT, { read: TemplateRef, static: true })
  _itemTemplate: TemplateRef<any>;

  @ContentChild(SELECTED_ITEM_CONTENT, { read: TemplateRef, static: true })
  _selectedItemTemplate: TemplateRef<any>;

  @ContentChild(SELECT_GROUP_CONTENT, { read: TemplateRef, static: true })
  _groupTemplate: TemplateRef<any>;

  @ContentChild(SELECT_FOOTER_CONTENT, { read: TemplateRef, static: true })
  _footerTemplate: TemplateRef<any>;

  /** View Part **/
  @ViewChild(MultiSelect) pMultiSelect: MultiSelect;
  @ViewChild(Select) pDropdown: Select;

  get showLoading(): boolean {
    return this.loading || this._showLoading;
  }
  private _showLoading = true;

  /** Implemented as part of FormFieldControl. */
  get id(): string {
    return this.inputId;
  }

  /** Implemented as part of FormFieldControl. */
  @Input()
  get required(): boolean {
    return (
      this._required ??
      this._ngControlRef.current?.control?.hasValidator(Validators.required) ??
      false
    );
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
  }
  private _required: boolean;

  private _ngControlRef = injectNgControlRef();
  /** Value accessor functions **/
  private _onChange: (value: any) => void;
  private _onTouch: () => void;
  protected dropDownOverlayOption: OverlayOptions = {
    baseZIndex: Z_INDEX_POPOVER + 1,
  };

  /** Read only props **/
  get selectedResultsLength() {
    return this.selectedResults?.length;
  }

  /** Component life cycle **/
  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isMultipleEnabled) {
      this.resetValues();
    }
    if (changes.selected) {
      if (this.isMultipleEnabled) {
        this.selectedOptionValues = Array.isArray(this.selected)
          ? this.selected
          : [];
        // this.onOptionChanged();
      } else {
        this.selectedOptionValue = this.selected;
        // this.onOptionChanged();
      }
      // When we have options exist and no search term, and the selected value is updated
      // We need to update the selectedResults that are bound to pDropdown & pMutiSelect to show the right values
      if (this.options?.length > 0 && !this._searchTerm.length) {
        this.updateSelectedResult();
      }
    }

    if (changes.options) {
      const res = changes.options.currentValue
        ? uniqBy(changes.options.currentValue, (item) =>
            this.getBoundedValue(item),
          )
        : [];
      this.options = [...res];
      // If there was value for selectedOptionValue but not for selected Result, get the initial value
      if (this.options?.length > 0 && !this._searchTerm.length) {
        this.updateSelectedResult();
      }

      if (changes.options.currentValue !== null) {
        this._showLoading = false;
      }
    }
  }

  /** Filtering functions **/
  onFilterOptions(event) {
    this._showLoading = this.isCustomFilter;
    this._searchTerm = this.isCustomFilter ? event : (event?.filter ?? '');
    this.onFilterChanged.emit(this._searchTerm);
  }

  onOptionChanged() {
    // this.onTouch();
    if (this.isMultipleEnabled) {
      const values =
        this.selectedResults?.map((opt) => this.getBoundedValue(opt)) ?? [];
      this.selectedOptionValues = values;
      this._onChange?.(values);
      this.onSelectionChanged.emit(values);
    } else {
      const boundedValue = this.getBoundedValue(this.selectedResult);
      this.selectedOptionValue = boundedValue;
      this._onChange?.(boundedValue);
      this.onSelectionChanged.emit(boundedValue);
    }
  }

  writeValue(value: any | any[]): void {
    //We have a problem that we write value before updating options, this causes the value to be lost
    //Todo in all write value function that has setTimeout we need to find another solution
    //For example we can store the value as property and when option is updated we can take the control value directly from this.value
    setTimeout(() => {
      if (value) {
        this.resetValues();
      }
      if (this.isMultipleEnabled) {
        this.selectedOptionValues = value;
        //Client side filtering
        if (!this.isCustomFilter && this.options?.length) {
          this.selectedResults = value?.map((v) =>
            this.options.find((i) => this.getBoundedValue(i) == v),
          );
        }
        //If empty options even in remote filtering remove all the selected options
        if (value?.length === 0) {
          this.selectedResults = [];
        }
      } else {
        this.selectedOptionValue = value;
        if (!this.isCustomFilter && this.options?.length) {
          this.setSelectedResult();
        }
      }
    });
  }

  setSelectedResult(): void {
    if (typeof this.selectedOptionValue === 'object') {
      // case 1 :object which doesn't have primary key property "such as Localized Value Object"
      // >>>> options: array of categories , bindValue:empty , bindKey: empty
      if (!this.bindValue && !this.bindKey) {
        this.selectedResult = this.options
          .filter((opt) =>
            isEqual(this.getBoundedValue(opt), this.selectedOptionValue),
          )
          ?.pop();
      }
      // case 2 :object which has primary key property "such as Entity which has Id property
      // as primary key property" >>>> options: array of entities , bindValue:empty , bindKey: id
      if (!this.bindValue && !!this.bindKey) {
        this.selectedResult = this.options
          .filter(
            (opt) =>
              this.getBoundedValue(opt)[this.bindKey] ==
              this.selectedOptionValue?.[this.bindKey],
          )
          ?.pop();
      }
      // case 3 :object which has an object property and we want to select items from this property,
      // so we will send  this property as bindValue and the bindKey is the primary key in this prop
      // such as 'moduleDetails' in entity >>>> options: array of entities , bindValue:moduleDetails , bindKey: id
      if (!!this.bindValue && !!this.bindKey) {
        const isBindKeyAvailable = !!Object.keys(
          this.getItemBoundedProp(this.selectedOptionValue, this.bindValue) ??
            {},
        ).find((key) => key == this.bindKey);
        if (isBindKeyAvailable) {
          this.selectedResult = this.options
            .filter(
              (opt) =>
                this.getBoundedValue(opt)[this.bindKey] ==
                this.selectedOptionValue?.[this.bindKey],
            )
            ?.pop();
        } else {
          this.selectedResult = null;
        }
      }
    } else if (
      (typeof this.selectedOptionValue === 'string' &&
        this.selectedOptionValue?.length) ||
      typeof this.selectedOptionValue === 'number'
    ) {
      // For grouped options, we need to search through the items within each group
      if (this.isGroupEnabled) {
        let foundItem = null;
        for (const group of this.options) {
          if (group.items && Array.isArray(group.items)) {
            foundItem = group.items.find(
              (item) => this.getBoundedValue(item) == this.selectedOptionValue,
            );
            if (foundItem) break;
          }
        }
        this.selectedResult = foundItem;
      } else {
        this.selectedResult = this.options
          .filter(
            (opt) => this.getBoundedValue(opt) == this.selectedOptionValue,
          )
          ?.pop();
      }
    } else {
      this.selectedResult = null;
    }
    this.cdr.detectChanges();
  }

  resetValues(): void {
    if (this.isMultipleEnabled) {
      this.selectedResults = [];
      this.selectedOptionValues = [];
    } else {
      this.selectedResult = null;
      this.selectedOptionValue = null;
    }
  }
  registerOnChange(fn: any): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this._onTouch = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onBlured() {
    this._onTouch?.();
    this.onBlur.emit();
  }
  onShowed() {
    this.onShow.emit();
  }
  /** Helpers **/
  getBoundedValue(item) {
    if (!this.bindValue || item == null) {
      return item;
    }
    return this.getItemBoundedProp(item, this.bindValue);
  }

  getBoundedAvatar(item) {
    return this.getItemBoundedProp(item, this.bindAvatar);
  }

  getBoundedCaption(item) {
    return this.getItemBoundedProp(item, this.bindCaption);
  }

  getBoundedLabel(item) {
    return this.getItemBoundedProp(item, this.bindLabel);
  }

  getBoundedGroupLabel(item) {
    return this.getItemBoundedProp(item, this.bindGroupLabel);
  }

  getBoundedIcon(item) {
    return this.getItemBoundedProp(item, this.bindIcon);
  }

  isString(arg) {
    return typeof arg === 'string';
  }

  private getItemBoundedProp(item, prop) {
    if (!prop) {
      return null;
    }
    if (typeof prop == 'function') {
      return prop(item);
    } else {
      return getPathValue(item, prop);
    }
  }

  private updateSelectedResult() {
    if (this._isMultipleEnabled) {
      //Set selectedResults on the first load
      if (this.selectedOptionValues?.length) {
        if (this.isGroupEnabled) {
          // For grouped options, search through all groups' items
          const allItems = this.options.reduce((acc, group) => {
            return group.items ? [...acc, ...group.items] : acc;
          }, []);
          this.selectedResults = allItems.filter((opt) =>
            this.selectedOptionValues.includes(this.getBoundedValue(opt)),
          );
        } else {
          this.selectedResults = this.options.filter((opt) =>
            this.selectedOptionValues.includes(this.getBoundedValue(opt)),
          );
        }
      } else {
        this.selectedResults = [];
      }
    } else {
      //Set selected result on the first option load
      this.setSelectedResult();
    }
  }
}
